import { useState } from 'react';
import { useUI } from '@/state/store';
import { useSSA } from './store';
import { DEFAULT_ZONES, validZones } from './monitoring';
import { useZoneProfiles } from './monitoringStore';

export function ProximityControls() {
  const selected = useUI((s) => s.selection.missionId),
    zones = useSSA((s) => s.zones);
  const animate = useSSA((s) => s.animateMotion),
    predict = useSSA((s) => s.predictEncounters);
  return (
    <details className="mt-3 border-t border-sky-200/15 pt-2">
      <summary className="cursor-pointer text-slate-300">Monitoring settings</summary>
      <ZoneEditor key={selected + ':' + JSON.stringify(zones)} selected={selected} />
      <label className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={animate}
          onChange={(e) => useSSA.setState({ animateMotion: e.target.checked })}
        />
        Animate direction and pulses
      </label>
      <p className="mt-1 text-[10px] text-slate-400">
        Pauses with the clock. Respects reduced-motion preferences.
      </p>
      <label className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={predict}
          onChange={(e) => useSSA.setState({ predictEncounters: e.target.checked })}
        />
        Predict close encounters
      </label>
      <p className="mt-1 text-[10px] text-slate-400">
        Next 10 minutes · whole catalogue, including hidden debris. Runs only for the selected object.
      </p>
    </details>
  );
}
function ZoneEditor({ selected }: { selected: string | null }) {
  const [draft, setDraft] = useState(useSSA.getState().zones),
    [error, setError] = useState('');
  const apply = () => {
    if (!validZones(draft)) {
      setError('Use inner < close < watch, from 1 to 50,000 km.');
      return;
    }
    useSSA.setState({ zones: draft });
    if (selected) useZoneProfiles.getState().save(selected, draft);
    setError('');
  };
  return (
    <div className="mt-2">
      <div className="grid grid-cols-3 gap-2">
        {(['watch', 'close', 'inner'] as const).map((key) => (
          <label key={key} className="text-[10px] capitalize text-slate-400">
            {key} · km
            <input
              aria-label={key + ' proximity threshold in kilometres'}
              type="number"
              min="1"
              max="50000"
              className="mt-1 w-full rounded border border-sky-200/20 bg-[#10202a] px-2 py-1.5 text-xs text-sky-100"
              value={draft[key]}
              onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })}
            />
          </label>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="rounded border border-sky-200/30 px-2 py-1" onClick={apply}>
          Apply zones
        </button>
        <button
          type="button"
          className="text-slate-400"
          onClick={() => {
            setDraft({ ...DEFAULT_ZONES });
            setError('');
          }}
        >
          Defaults
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-[10px] text-amber-200">
          {error}
        </p>
      )}
      <p className="mt-2 text-[10px] text-slate-400">
        Attention zones, not collision-risk limits.{' '}
        {selected ? 'Saved for this object.' : 'Select an object to save its zones.'}
      </p>
    </div>
  );
}
